import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

export interface ScrollRestorationContextValue {
  registerScrollContainer: (scope: string, element: HTMLElement | null) => void;
  getScrollContainer: (scope: string) => HTMLElement | null;
}

const ScrollRestorationContext =
  createContext<ScrollRestorationContextValue | null>(null);

export const ScrollRestorationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const containersRef = useRef<Record<string, HTMLElement>>({});

  const registerScrollContainer = useCallback(
    (scope: string, element: HTMLElement | null) => {
      if (!scope) return;
      if (element) {
        containersRef.current[scope] = element;
      } else {
        delete containersRef.current[scope];
      }
    },
    [],
  );

  const getScrollContainer = useCallback((scope: string) => {
    return containersRef.current[scope] || null;
  }, []);

  const value = useMemo(
    () => ({
      registerScrollContainer,
      getScrollContainer,
    }),
    [registerScrollContainer, getScrollContainer],
  );

  return (
    <ScrollRestorationContext.Provider value={value}>
      {children}
    </ScrollRestorationContext.Provider>
  );
};

export const useScrollRestorationContext =
  (): ScrollRestorationContextValue | null =>
    useContext(ScrollRestorationContext);
